import { Card, Flex, SimpleTable, Typography } from "djuno-design";
import { useState } from "react";
import ExpandableCard from "../components/ExpandableCard";
import { GithubTypesLink } from "./../urls";

const { Title, Text } = Typography;

const CardPage = () => {
  const [btnLoading, setBtnLoading] = useState(false);
  const handleClick = () => {
    setBtnLoading(true);
    setTimeout(() => setBtnLoading(false), 3000);
  };

  return (
    <div style={{ width: "100%", height: "100%" }}>
      <Flex direction="col">
        <Flex direction="col">
          <Title level={4}>Card</Title>
          <Text size="sm" style={{ marginBottom: 30 }}>
            The Card component in Djuno Design provides a flexible and
            customizable container that can display content in a structured,
            card-like format. It is ideal for grouping information, settings,
            and actions, with options to display a title, description, and
            additional settings.
          </Text>

          <Title level={5}>Variants and Customization</Title>
          <Text size="sm">
            The Card component offers various customization options to fit your
            specific needs:
          </Text>
          <ul className="marker:text-sky-400 list-disc pl-6">
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Title and Description
                </Text>
                : You can provide a title and a description to give users
                context for the content within the card. The title can be
                customized to different levels of heading tags (h1 to h6),
                allowing for flexible styling
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Setting Area
                </Text>
                : A dedicated setting area allows you to place additional
                controls or actions, such as buttons or icons, in the top-right
                corner of the card
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Customizable Layout
                </Text>
                : The card layout is fully customizable using class names for
                different sections (header, title, description), so you can
                easily match the design to your application's style.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Content Rendering
                </Text>
                : The main content is passed as children, allowing you to place
                any custom elements, text, or components within the card body
              </Text>
            </li>
          </ul>

          <Title level={5} className="mt-4">
            Additional Features
          </Title>
          <ul className="marker:text-sky-400 list-disc pl-6">
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Responsive Design
                </Text>
                : The Card is built with responsive design in mind, ensuring it
                adapts well to different screen sizes and device types.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Light and Dark Themes
                </Text>
                : The component supports light and dark themes, automatically
                adjusting the background and border colors to fit the current
                theme.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Border and Padding
                </Text>
                : The Card comes with rounded corners and subtle borders by
                default, providing a clean and modern look. It also includes
                customizable padding for better content spacing.
              </Text>
            </li>
          </ul>
        </Flex>

        {/* Examples */}
        <Flex direction="col" className="w-full">
          <Title level={5} className="mt-5">
            Examples
          </Title>
          <ExpandableCard
            codeViewerProps={{
              code: CardExample1,
            }}
          >
            <Flex direction="col" className="gap-4 w-full">
              <Flex
                direction={{ default: "col", lg: "row" }}
                className="gap-4 w-full"
              >
                <Card title="Title" description="description" setting="setting">
                  content
                  <br />
                  Lorem ipsum dolor sit amet consectetur adipisicing elit.
                  Asperiores minus enim velit dignissimos, sunt nemo quis nihil.
                  Ab nisi error, sed fugit quia laborum nostrum temporibus,
                  magni quo, doloribus voluptatum?
                </Card>
              </Flex>
            </Flex>
          </ExpandableCard>
        </Flex>

        {/* API */}
        <Flex direction="col" className="w-full mt-5">
          <Title level={5} className="">
            API
          </Title>
          <Flex direction="col" className="gap-1 w-full">
            <Card id="API">
              <SimpleTable>
                <SimpleTable.Head>
                  <SimpleTable.TH>Property</SimpleTable.TH>
                  <SimpleTable.TH>Description</SimpleTable.TH>
                  <SimpleTable.TH>Type</SimpleTable.TH>
                  <SimpleTable.TH>Default</SimpleTable.TH>
                </SimpleTable.Head>
                <SimpleTable.Body>
                  {/* id */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">id</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">Unique identifier</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        string
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* children */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">children</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">The content inside the card</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        React.ReactNode
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* title */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">title</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        The title displayed at the top of the card. It can be a
                        string or a React node for more complex layouts.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        {`string | React.ReactNode`}
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* titleLevel */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">titleLevel</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">{`Determines the heading level for the title. It defaults to 5 (renders as <h5>).`}</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link
                        target="_blank"
                        href={GithubTypesLink + "Typography.ts#L24"}
                        size="xs"
                        code
                      >
                        TypographyTitleLevels
                      </Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">5</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* description */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">description</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        A description that provides additional context or
                        details below the title.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>{`string | React.ReactNode`}</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD></SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* setting */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">setting</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        A custom element or action (e.g., button, icon)
                        displayed in the top-right corner of the card.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>{`React.ReactNode`}</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* className */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">className</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Additional class names to customize the card's
                        appearance.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        string
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD></SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* headerClassName */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">headerClassName</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Class names for customizing the appearance of the card
                        header section.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        string
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD></SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* titleClassName */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">titleClassName</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Class names for customizing the appearance of the title.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        string
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD></SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* descriptionClassName */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">descriptionClassName</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Class names for customizing the appearance of the
                        description.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        string
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD></SimpleTable.TD>
                  </SimpleTable.Row>
                </SimpleTable.Body>
              </SimpleTable>
            </Card>
          </Flex>
        </Flex>
      </Flex>
    </div>
  );
};

const CardExample1 = `
  <Card title="Title" description="description" setting="setting">
    content
    <br />
    Lorem ipsum dolor sit amet consectetur adipisicing elit.
    Asperiores minus enim velit dignissimos, sunt nemo quis nihil.
    Ab nisi error, sed fugit quia laborum nostrum temporibus, magni
    quo, doloribus voluptatum?
  </Card>
`;

export default CardPage;
